/**
 * The dashed add card at the end of a card grid (TMMAPP-0088, pattern 5; visual contract §6,
 * TMMAPP-0099). A button, so a guest's subscriber action can open purchase instead of a disabled
 * control. Callers pass the label.
 *
 * Transparent with a 1px dashed `line` border at `radius.card`, padding 20, `link` text, its
 * contents left-aligned at gap 5 in a vertically centred stack: a "+"
 * glyph at 26/300, the title 13/500 in sentence case (contract v2 §7), the description `caption` in `text2`. Hover
 * is the `selected` fill with a `brandBoundary` border; the fill is painted over an opaque
 * `surface` so the label stays on the guaranteed `link / surface + selected` pair on the grey page.
 * The border is decorative: the label names the control. `size="compact"` is the category card's
 * add card (min-height 104, padding 16 18, "+" 22/300, gap 4).
 */
import { useId, type MouseEvent } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { resolveTokens } from '@/theme/theme';

interface AddNewCardProps {
  label: string;
  hint?: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Matches the height of the cards it sits beside (220 envelopes, 200 accounts). */
  minHeight?: number | string;
  size?: 'regular' | 'compact';
  /** Another element's id that also describes the button (a subscriber-only hint). */
  describedBy?: string | undefined;
}

export function AddNewCard({
  label,
  hint,
  onClick,
  minHeight,
  size = 'regular',
  describedBy,
}: AddNewCardProps) {
  const tokens = resolveTokens(useTheme());
  const hintId = useId();
  const described = hint !== undefined && hint !== '';
  const compact = size === 'compact';
  return (
    <Button
      variant="text"
      onClick={onClick}
      aria-label={label}
      aria-describedby={
        [described ? hintId : undefined, describedBy].filter(Boolean).join(' ') || undefined
      }
      data-size={size}
      sx={{
        height: '100%',
        minHeight: minHeight ?? (compact ? 104 : 0),
        width: '100%',
        flexDirection: 'column',
        // Left-aligned in a vertically centred stack (P:294 / 584 / 1188).
        alignItems: 'flex-start',
        textAlign: 'start',
        justifyContent: 'center',
        gap: compact ? '4px' : '5px',
        p: compact ? '16px 18px' : '20px',
        border: `1px dashed ${tokens.line}`,
        borderRadius: `${tokens.radius.card}px`,
        bgcolor: 'transparent',
        backgroundImage: 'none',
        boxShadow: 'none',
        color: tokens.link,
        textTransform: 'none',
        letterSpacing: 0,
        '&:hover': {
          bgcolor: tokens.surface,
          backgroundImage: `linear-gradient(${tokens.selected}, ${tokens.selected})`,
          borderColor: tokens.brandBoundary,
          borderStyle: 'dashed',
        },
        '&:focus-visible': {
          outline: `2px solid ${tokens.focusRing}`,
          outlineOffset: 2,
        },
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          fontSize: compact ? 22 : 26,
          lineHeight: 1,
          fontWeight: 300,
          color: 'inherit',
        }}
      >
        +
      </Box>
      <Typography
        variant="body2"
        component="span"
        sx={{
          fontSize: 13,
          lineHeight: '20px',
          fontWeight: 500,
          letterSpacing: 0,
          textTransform: 'none',
          color: 'inherit',
        }}
      >
        {label}
      </Typography>
      {described && (
        <Typography id={hintId} variant="caption" component="span" sx={{ color: tokens.text2 }}>
          {hint}
        </Typography>
      )}
    </Button>
  );
}
